import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useKit } from "@/context/KitContext";
import { KitItemRow } from "@/components/KitPanel";

const FIELDS = [
  ["full_name", "Full name", true, "sm:col-span-2"],
  ["phone", "Phone number", true, "sm:col-span-2"],
  ["address_line1", "Address line 1", true, "sm:col-span-2"],
  ["address_line2", "Address line 2 (optional)", false, "sm:col-span-2"],
  ["city", "City", true, ""],
  ["state", "State / Region", true, ""],
  ["postal_code", "Postal code", true, ""],
  ["country", "Country", true, ""],
];

const Field = ({ name, label, required, cls, value, onChange }) => (
  <label className={`block ${cls}`}>
    <span className="text-xs font-mono uppercase tracking-widest text-slate-500">{label}</span>
    <input
      name={name} required={required} value={value} onChange={onChange} data-testid={`delivery-${name.replace(/_/g, "-")}-input`}
      className="mt-2 w-full h-12 px-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#4285F4] focus:border-transparent"
    />
  </label>
);

export default function Checkout() {
  const { user, refresh } = useAuth();
  const { items, total, clear } = useKit();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: user.name || "", notes: "", ...Object.fromEntries(FIELDS.slice(1).map(([k]) => [k, ""])) });
  const [loading, setLoading] = useState(false);

  if (user.has_order) return <Navigate to="/my-order" replace />;
  if (!items.length) return <Navigate to="/" replace />;

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { items: items.map(({ product_id, color, size }) => ({ product_id, color, size })), delivery: form };
      await api.post("/orders", payload);
      clear();
      await refresh();
      toast.success("Order placed!");
      navigate("/my-order");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-10">
      <Link to="/" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="checkout-back-link">
        <ArrowLeft className="w-4 h-4" /> Back to store
      </Link>
      <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">Delivery details</h1>
      <form onSubmit={submit} className="grid lg:grid-cols-12 gap-8" data-testid="checkout-form">
        <div className="lg:col-span-7 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 grid sm:grid-cols-2 gap-5">
          {FIELDS.map(([name, label, req, cls]) => (
            <Field key={name} name={name} label={label} required={req} cls={cls} value={form[name]} onChange={onChange} />
          ))}
          <label className="block sm:col-span-2">
            <span className="text-xs font-mono uppercase tracking-widest text-slate-500">Delivery notes (optional)</span>
            <textarea
              name="notes" rows={3} value={form.notes} onChange={onChange} data-testid="delivery-notes-input"
              className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#4285F4]"
            />
          </label>
        </div>
        <aside className="lg:col-span-5 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 h-fit lg:sticky lg:top-24 space-y-5">
          <h2 className="font-heading text-2xl font-bold">Order summary</h2>
          <ul className="divide-y divide-slate-100">{items.map((i) => <KitItemRow key={i.key} item={i} />)}</ul>
          <div className="flex justify-between border-t border-slate-200 pt-4">
            <span className="text-slate-600">Total</span>
            <span className="font-mono font-semibold" data-testid="checkout-total-points">{total} / {user.points} pts</span>
          </div>
          <p className="text-xs text-slate-500">You can place only one order. Double-check your sizes and address.</p>
          <button
            type="submit" disabled={loading} data-testid="checkout-submit-btn"
            className="w-full h-14 rounded-full bg-[#1A73E8] hover:bg-[#1557B0] text-white font-semibold flex items-center justify-center gap-2 transition-colors active:scale-[0.98] disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Place order"}
          </button>
        </aside>
      </form>
    </div>
  );
}
